import { useEffect, useState } from "react";
import { Save, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Btn, Field, Select } from "@/components/admin/ui";
import RichTextEditor from "@/components/editor/RichTextEditor";

const DEF = {
  eyebrow: "Get in Touch", heading: "Contact Us",
  intro: "<p>Thank you for your consideration. Please see our contact information below, or fill out the form.</p>",
  phone_card_label: "Call Our Office", email_card_label: "Email Us",
  location_card_label: "Service Area", appointment_card_label: "By Appointment",
  show_phone_card: true, show_email_card: true, show_location_card: true, show_appointment_card: true,
  form_heading: "Leave a message",
  form_note: "This form sends an enquiry to our team — it does not complete a booking. We will contact you to confirm.",
  appointment_tab_label: "Book an Appointment", contact_tab_label: "General Enquiry",
  submit_appointment_label: "Request Appointment", submit_contact_label: "Send Message",
  success_message: "",
};

const Toggle = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center gap-2 text-sm text-emerald-900">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} className="accent-emerald-700" /> {label}
  </label>
);

export default function ContactPage() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return <div className="text-emerald-800">Loading…</div>;

  const c = { ...DEF, ...(s.contact || {}) };
  const setC = (patch) => setS({ ...s, contact: { ...c, ...patch } });

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/admin/settings", { ...s, contact: c }); setS(data); toast.success("Contact page saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-contact-page">
      <PageHeader title="Contact Page" subtitle="Manage the public Contact page content and info cards">
        <a href="/contact" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-lg border border-stone-300 px-4 py-2 text-sm font-semibold hover:bg-stone-100 text-emerald-900"><ExternalLink size={16} /> View</a>
        <Btn onClick={save} disabled={saving} data-testid="save-contact-btn"><Save size={16} /> {saving ? "Saving…" : "Save"}</Btn>
      </PageHeader>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 border border-stone-200/80 space-y-4">
          <h3 className="font-serif text-lg text-emerald-900">Heading & intro</h3>
          <Field label="Eyebrow (small label)" value={c.eyebrow} onChange={(v) => setC({ eyebrow: v })} />
          <Field label="Page heading" value={c.heading} onChange={(v) => setC({ heading: v })} />
          <div><span className="text-sm font-medium text-emerald-900">Introductory text</span><div className="mt-1"><RichTextEditor value={c.intro} onChange={(v) => setC({ intro: v })} /></div></div>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-stone-200/80 space-y-4 h-fit">
          <h3 className="font-serif text-lg text-emerald-900">Contact information cards</h3>
          <p className="text-xs text-stone-500">Phone, email, address and the appointment notice come from <b>Settings → Business</b> and are reused here. Edit the card labels and visibility below.</p>
          <div className="space-y-3">
            <div className="flex items-end gap-2"><div className="flex-1"><Field label="Telephone card label" value={c.phone_card_label} onChange={(v) => setC({ phone_card_label: v })} /></div><Toggle label="Show" checked={c.show_phone_card} onChange={(v) => setC({ show_phone_card: v })} testid="toggle-phone-card" /></div>
            <div className="flex items-end gap-2"><div className="flex-1"><Field label="Email card label" value={c.email_card_label} onChange={(v) => setC({ email_card_label: v })} /></div><Toggle label="Show" checked={c.show_email_card} onChange={(v) => setC({ show_email_card: v })} testid="toggle-email-card" /></div>
            <div className="flex items-end gap-2"><div className="flex-1"><Field label="Location card label" value={c.location_card_label} onChange={(v) => setC({ location_card_label: v })} /></div><Toggle label="Show" checked={c.show_location_card} onChange={(v) => setC({ show_location_card: v })} testid="toggle-location-card" /></div>
            <div className="flex items-end gap-2"><div className="flex-1"><Field label="Appointment card label" value={c.appointment_card_label} onChange={(v) => setC({ appointment_card_label: v })} /></div><Toggle label="Show" checked={c.show_appointment_card} onChange={(v) => setC({ show_appointment_card: v })} testid="toggle-appointment-card" /></div>
          </div>
          <div className="pt-3 border-t border-stone-200 grid sm:grid-cols-2 gap-3">
            <Field label="Phone (display)" value={s.phone} onChange={(v) => setS({ ...s, phone: v })} />
            <Field label="Phone link (tel:)" value={s.phone_tel} onChange={(v) => setS({ ...s, phone_tel: v })} />
            <Field label="Email" value={s.email} onChange={(v) => setS({ ...s, email: v })} />
            <Field label="Address / service area" value={s.address} onChange={(v) => setS({ ...s, address: v })} />
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-stone-200/80 space-y-4 lg:col-span-2">
          <h3 className="font-serif text-lg text-emerald-900">Enquiry form</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Form heading" value={c.form_heading} onChange={(v) => setC({ form_heading: v })} />
            <Field label="Form note (under the message)" value={c.form_note} onChange={(v) => setC({ form_note: v })} />
            <Field label="Appointment tab label" value={c.appointment_tab_label} onChange={(v) => setC({ appointment_tab_label: v })} />
            <Field label="General-enquiry tab label" value={c.contact_tab_label} onChange={(v) => setC({ contact_tab_label: v })} />
            <Field label="Submit label (appointment)" value={c.submit_appointment_label} onChange={(v) => setC({ submit_appointment_label: v })} />
            <Field label="Submit label (general)" value={c.submit_contact_label} onChange={(v) => setC({ submit_contact_label: v })} />
            <Field label="Success message (optional override)" value={c.success_message} onChange={(v) => setC({ success_message: v })} />
          </div>
        </div>
      </div>
    </div>
  );
}
